import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import * as api from '../services/api'
import type { CurrentUser } from '../services/api'

// Shared, module-level session state. The provider session lives in an
// HttpOnly cookie, so we mirror "who am I" here by probing GET /api/me once
// and caching the result.
const currentUser = ref<CurrentUser | null>(null)
let loaded = false
let inflight: Promise<void> | null = null

/** Probe the session once (or again with force). Safe to call from anywhere. */
export async function fetchMe(force = false): Promise<void> {
  if (loaded && !force) return
  if (inflight) return inflight
  inflight = (async () => {
    try {
      currentUser.value = await api.getMe()
    } catch {
      currentUser.value = null
    } finally {
      loaded = true
      inflight = null
    }
  })()
  return inflight
}

export function getCurrentUser(): CurrentUser | null {
  return currentUser.value
}

export function useAuth() {
  const router = useRouter()

  const isAuthenticated = computed(() => !!currentUser.value)
  const currentUserId = computed(() => currentUser.value?.id ?? null)

  function setUser(user: CurrentUser | null): void {
    currentUser.value = user
    loaded = true
  }

  async function logout(): Promise<void> {
    try {
      await api.logout()
    } catch {
      // best effort — clear local state regardless
    }
    currentUser.value = null
    loaded = true
    router.push('/login')
  }

  function clearAuthOnUnauthorized(): void {
    currentUser.value = null
    loaded = true
  }

  return { currentUser, isAuthenticated, currentUserId, fetchMe, setUser, logout, clearAuthOnUnauthorized }
}
